import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmModal } from "../components/common";
import { useAuth } from "../contexts/AuthContext";
import { useCourseDetail } from "../hooks/useCourses";
import applicationService, {
  CreateApplicationRequest,
  Document,
  DocumentType,
  Gender,
  RequiredDocument,
  UpdateApplicationRequest,
} from "../services/api/applicationService";

type Step = "personal" | "documents" | "content" | "review";

const ApplicationForm: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { course, loading: courseLoading } = useCourseDetail(courseId || "");

  const [currentStep, setCurrentStep] = useState<Step>("personal");
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);

  // Form data
  const [personalData, setPersonalData] = useState<CreateApplicationRequest>({
    courseId: courseId || "",
    givenNames: "",
    familyName: "",
    gender: "MALE",
    dateOfBirth: "",
    nationality: "",
    placeOfBirth: "",
    email: user?.email || "",
    whatsappNumber: "",
    passportNumber: "",
    passportExpiry: "",
  });

  const [contentData, setContentData] = useState<UpdateApplicationRequest>({
    personalStatement: "",
    academicHistory: "",
    workExperience: "",
    references: "",
  });

  const [uploadedFiles, setUploadedFiles] = useState<{
    [key in DocumentType]?: File[];
  }>({});

  const [existingDocuments, setExistingDocuments] = useState<Document[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);
  const [documentErrors, setDocumentErrors] = useState<{
    [key in DocumentType]?: string;
  }>({});

  // Submission success state
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  // Transition state
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Modal state
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [pendingDraftId, setPendingDraftId] = useState<string | null>(null);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/signin?redirect=/apply/${courseId}`);
    }
  }, [isAuthenticated, navigate, courseId]);

  // Load required documents
  useEffect(() => {
    if (courseId) {
      loadRequiredDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Check for existing draft application or resume from query params
  useEffect(() => {
    if (courseId && isAuthenticated) {
      const qAppId = searchParams.get("applicationId");
      const qStep = searchParams.get("step") as Step | null;

      if (qAppId && qStep) {
        // Resuming from payment cancel — load application and jump to target step
        setApplicationId(qAppId);
        loadExistingApplication(qAppId, qStep);
      } else {
        checkExistingDraft();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, isAuthenticated]);

  // Load documents when entering documents step
  useEffect(() => {
    if (currentStep === "documents" && applicationId) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, applicationId]);

  // Update email when user loads
  useEffect(() => {
    if (user?.email && !personalData.email) {
      setPersonalData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const loadRequiredDocuments = async () => {
    try {
      const response = await applicationService.getRequiredDocuments(courseId!);
      setRequiredDocuments(response.requiredDocuments);
    } catch (err: any) {
      console.error("Failed to load required documents:", err);
    }
  };

  const checkExistingDraft = async () => {
    try {
      const draftId = await applicationService.hasExistingDraftApplication(
        courseId!
      );
      if (draftId) {
        setPendingDraftId(draftId);
        setShowDraftModal(true);
      }
    } catch (err) {
      console.error("Error checking for existing draft:", err);
    }
  };

  const loadExistingApplication = async (id: string, targetStep: Step = "documents") => {
    try {
      const app = await applicationService.getApplicationById(id);

      // Populate form with existing data
      if (app.givenNames) {
        setPersonalData({
          courseId: courseId || "",
          givenNames: app.givenNames,
          familyName: app.familyName || "",
          gender: app.gender || "MALE",
          dateOfBirth: app.dateOfBirth || "",
          nationality: app.nationality || "",
          placeOfBirth: app.placeOfBirth || "",
          email: app.email || user?.email || "",
          whatsappNumber: app.whatsappNumber || "",
          passportNumber: app.passportNumber || "",
          passportExpiry: app.passportExpiry || "",
        });
      }

      setContentData({
        personalStatement: app.personalStatement || "",
        academicHistory: app.academicHistory || "",
        workExperience: app.workExperience || "",
        references: app.references || "",
      });

      // Load existing documents
      setExistingDocuments(app.documents || []);

      setCurrentStep(targetStep);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadDocuments = async () => {
    if (!applicationId) return;
    try {
      const response = await applicationService.getDocuments(applicationId);
      setExistingDocuments(response.documents || []);
    } catch (err: any) {
      console.error("Failed to load documents:", err);
    }
  };

  const transitionToStep = (step: Step) => {
    setIsTransitioning(true);
    setTimeout(() => {
      window.scrollTo(0, 0);
      setCurrentStep(step);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsTransitioning(false));
      });
    }, 300);
  };

  const handlePersonalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (applicationId) {
        // Application already exists, just move to next step
        transitionToStep("documents");
      } else {
        // Create new application
        const response = await applicationService.createApplication(
          personalData
        );
        setApplicationId(response.id);
        setSuccess("Application created successfully!");
        setTimeout(() => {
          setSuccess(null);
          transitionToStep("documents");
        }, 1500);
      }
    } catch (err: any) {
      if (err.validationErrors?.length) {
        setError(err.validationErrors.map((e: string) => `• ${e}`).join('\n'));
      } else {
        setError(err.message || "Failed to save personal information");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!applicationId) {
      setError("Please complete personal information first");
      return;
    }

    setLoading(true);
    setError(null);
    setDocumentErrors({}); // Clear previous document-specific errors

    try {
      // Delete marked documents first
      for (const docId of documentsToDelete) {
        await applicationService.deleteDocument(applicationId, docId);
      }
      setDocumentsToDelete([]);

      // Only upload NEW files (files that user just selected)
      const hasNewFiles = Object.values(uploadedFiles).some(
        (files) => files && files.length > 0
      );

      if (hasNewFiles) {
        const successfulUploads: string[] = [];
        const failedUploads: { [key: string]: string } = {};

        for (const [docType, files] of Object.entries(uploadedFiles)) {
          if (files && files.length > 0) {
            try {
              // Check file sizes before upload
              const oversizedFiles = files.filter(
                (file) => file.size > 10 * 1024 * 1024
              ); // 10MB limit
              if (oversizedFiles.length > 0) {
                const oversizedNames = oversizedFiles
                  .map((f) => f.name)
                  .join(", ");
                throw new Error(
                  `File(s) too large: ${oversizedNames}. Maximum size is 10MB per file.`
                );
              }

              await applicationService.uploadDocuments(
                applicationId,
                files,
                Array(files.length).fill(docType as DocumentType)
              );
              // Track successful upload
              successfulUploads.push(docType);
              console.log(`✓ Successfully uploaded ${docType}`);
            } catch (uploadErr: any) {
              console.error(`✗ Failed to upload ${docType}:`, uploadErr);

              // Parse different error types
              let errorMessage = uploadErr.message || "Upload failed";
              if (
                uploadErr.message?.includes("413") ||
                uploadErr.message?.includes("Request Entity Too Large")
              ) {
                errorMessage = "File too large. Maximum size is 10MB per file.";
              } else if (uploadErr.message?.includes("400")) {
                errorMessage =
                  "Invalid file format. Only PDF, JPEG, PNG files are allowed.";
              } else if (
                uploadErr.message?.includes("network") ||
                uploadErr.message?.includes("Network")
              ) {
                errorMessage =
                  "Network error. Please check your connection and try again.";
              }

              // Store document-specific error
              failedUploads[docType] = errorMessage;
            }
          }
        }

        // Update document errors state
        if (Object.keys(failedUploads).length > 0) {
          setDocumentErrors(failedUploads);
        }

        // Remove successfully uploaded files from state
        if (successfulUploads.length > 0) {
          setUploadedFiles((prev) => {
            const updated = { ...prev };
            successfulUploads.forEach((type) => {
              delete updated[type as DocumentType];
            });
            return updated;
          });
        }

        // If some uploads failed, don't proceed to next step
        if (Object.keys(failedUploads).length > 0) {
          setLoading(false);
          return;
        }

        // Clear all uploaded files only if ALL uploads were successful
        setUploadedFiles({});
      }

      // Reload documents to get updated list
      await loadDocuments();

      setSuccess("Documents updated successfully!");
      setTimeout(() => {
        setSuccess(null);
        transitionToStep("content");
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to upload documents");
    } finally {
      setLoading(false);
    }
  };

  const handleContentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationId) return;

    setLoading(true);
    setError(null);

    try {
      await applicationService.updateApplication(applicationId, contentData);
      setSuccess("Application content saved!");
      setTimeout(() => {
        setSuccess(null);
        transitionToStep("review");
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to save application content");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!applicationId) return;

    setLoading(true);
    setError(null);

    try {
      // First validate
      const validation = await applicationService.validateApplication(
        applicationId
      );
      if (!validation.canProceedToPayment) {
        setError(
          `Missing required documents: ${validation.missingDocuments?.join(
            ", "
          )}`
        );
        return;
      }

      // Submit for review (no payment needed at this stage)
      await applicationService.submitApplication(applicationId);
      setSubmissionSuccess(true);
    } catch (err: any) {
      setError(err.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (docType: DocumentType, files: FileList | null) => {
    if (files) {
      // Clear any previous errors for this document type
      setDocumentErrors((prev) => {
        const updated = { ...prev };
        delete updated[docType];
        return updated;
      });

      // Validate files before setting state
      const validFiles: File[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        // Check file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          errors.push(
            `${file.name} is too large (${(file.size / 1024 / 1024).toFixed(
              2
            )}MB). Maximum size is 10MB.`
          );
          return;
        }

        // Check file type
        const allowedTypes = [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];
        if (!allowedTypes.includes(file.type)) {
          errors.push(
            `${file.name} has invalid format. Only PDF, JPEG, PNG files are allowed.`
          );
          return;
        }

        validFiles.push(file);
      });

      // If there are validation errors, show them
      if (errors.length > 0) {
        setDocumentErrors((prev) => ({
          ...prev,
          [docType]: errors.join(" "),
        }));
        return;
      }

      // Set valid files
      setUploadedFiles((prev) => ({
        ...prev,
        [docType]: validFiles,
      }));
    }
  };

  const handleDeleteExistingDocument = (documentId: string) => {
    if (!applicationId) return;
    setPendingDeleteDocId(documentId);
    setShowDeleteDocModal(true);
  };

  const confirmDeleteDocument = async () => {
    if (!applicationId || !pendingDeleteDocId) return;
    setShowDeleteDocModal(false);

    try {
      setLoading(true);
      await applicationService.deleteDocument(applicationId, pendingDeleteDocId);
      setExistingDocuments((prev) =>
        prev.filter((doc) => doc.id !== pendingDeleteDocId)
      );
      setSuccess("Document deleted successfully!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to delete document");
    } finally {
      setLoading(false);
      setPendingDeleteDocId(null);
    }
  };

  const handleConfirmDraft = () => {
    if (pendingDraftId) {
      setApplicationId(pendingDraftId);
      loadExistingApplication(pendingDraftId);
    }
    setShowDraftModal(false);
    setPendingDraftId(null);
  };

  const getExistingDocumentsForType = (docType: DocumentType) => {
    return existingDocuments.filter((doc) => doc.documentType === docType);
  };

  const handleBackToDocuments = () => {
    // Load documents when going back to documents step
    if (applicationId) {
      loadDocuments();
    }
    transitionToStep("documents");
  };

  const handleSaveDraft = async () => {
    setSuccess("Draft saved! You can continue later from My Applications.");
    setTimeout(() => {
      navigate("/my-applications");
    }, 2000);
  };

  if (courseLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Course Not Found
          </h1>
          <button
            onClick={() => navigate("/courses")}
            className="text-cyan-600 hover:text-cyan-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const steps: { id: Step; label: string; icon: React.ReactNode }[] = [
    {
      id: "personal",
      label: "Personal Details",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      id: "documents",
      label: "Documents",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "content",
      label: "Application Content",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
    },
    {
      id: "review",
      label: "Review",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
    },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Application for {course.title}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            {course.university.name} • {course.level}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          {/* Mobile: Vertical Progress Indicator */}
          <div className="block lg:hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Step {currentStepIndex + 1} of {steps.length}
              </h3>
              <span className="text-sm text-gray-500">
                {Math.round(((currentStepIndex + 1) / steps.length) * 100)}%
                Complete
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="bg-gradient-to-r from-cyan-600 to-cyan-500 h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{
                  width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
                }}
              ></div>
            </div>

            {/* Current Step Info */}
            <div className="flex items-center space-x-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center text-white">
                {steps[currentStepIndex].icon}
              </div>
              <div>
                <p className="font-medium text-cyan-900">
                  {steps[currentStepIndex].label}
                </p>
                <p className="text-sm text-cyan-700">Currently active</p>
              </div>
            </div>

            {/* Mini Steps Indicator */}
            <div className="flex justify-center mt-4 space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${index <= currentStepIndex ? "bg-cyan-600" : "bg-gray-300"
                    }`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Horizontal Stepper */}
          <div className="hidden lg:block">
            <div className="flex items-start justify-between relative">
              {steps.map((step, index) => {
                const isActive = index === currentStepIndex;
                const isCompleted = index < currentStepIndex;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const isUpcoming = index > currentStepIndex;

                return (
                  <div
                    key={step.id}
                    className="flex items-center flex-1 relative"
                  >
                    {/* Connector Line - positioned behind the circle */}
                    {index < steps.length - 1 && (
                      <div className="absolute top-6 left-1/2 w-full h-0.5 -z-10 flex items-center">
                        <div
                          className={`h-0.5 w-full transition-all duration-300 ${index < currentStepIndex
                            ? "bg-gradient-to-r from-green-500 to-cyan-500"
                            : "bg-gray-300"
                            }`}
                        />
                      </div>
                    )}

                    <div className="flex flex-col items-center w-full">
                      {/* Step Circle */}
                      <div
                        className={`relative w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 z-10 border-4 ${isCompleted
                          ? "bg-green-600 text-white shadow-lg border-green-200"
                          : isActive
                            ? "bg-cyan-600 text-white shadow-lg border-cyan-200"
                            : "bg-white text-gray-500 border-gray-300"
                          }`}
                      >
                        {isCompleted ? (
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          step.icon
                        )}

                        {/* Active Pulse Effect */}
                        {isActive && (
                          <div className="absolute inset-0 rounded-full bg-cyan-600 animate-ping opacity-30"></div>
                        )}
                      </div>

                      {/* Step Label */}
                      <span
                        className={`text-xs mt-3 text-center font-medium transition-colors duration-200 leading-tight ${isCompleted
                          ? "text-green-600"
                          : isActive
                            ? "text-cyan-600"
                            : "text-gray-500"
                          }`}
                        style={{ maxWidth: "80px" }}
                      >
                        {step.label}
                      </span>

                      {/* Step Number */}
                      <span
                        className={`text-xs mt-2 px-2 py-1 rounded-full font-medium ${isCompleted
                          ? "bg-green-100 text-green-700"
                          : isActive
                            ? "bg-cyan-100 text-cyan-700"
                            : "bg-gray-100 text-gray-500"
                          }`}
                      >
                        Step {index + 1}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Progress Stats */}
            <div className="mt-8 flex justify-between items-center text-sm bg-gray-50 rounded-lg p-4">
              <span className="text-gray-600 font-medium">
                Progress: {currentStepIndex + 1} of {steps.length} steps
                completed
              </span>
              <div className="flex items-center space-x-3">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-600 to-cyan-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${((currentStepIndex + 1) / steps.length) * 100
                        }%`,
                    }}
                  ></div>
                </div>
                <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full font-medium text-xs">
                  {Math.round(((currentStepIndex + 1) / steps.length) * 100)}%
                  Complete
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 sm:mb-6 text-sm sm:text-base whitespace-pre-line">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 sm:mb-6 text-sm sm:text-base">
            {success}
          </div>
        )}

        {/* Form Content */}
        <div className={`relative bg-white rounded-lg shadow-sm p-4 sm:p-6 transition-all duration-300 ease-out ${isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10 rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                <p className="text-sm text-gray-500">Processing...</p>
              </div>
            </div>
          )}
          {/* Step 1: Personal Details */}
          {currentStep === "personal" && (
            <form onSubmit={handlePersonalSubmit}>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                Personal Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Given Name(s) *
                  </label>
                  <input
                    type="text"
                    required
                    value={personalData.givenNames}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        givenNames: e.target.value,
                      })
                    }
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Family Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={personalData.familyName}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        familyName: e.target.value,
                      })
                    }
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender *
                  </label>
                  <select
                    required
                    value={personalData.gender}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        gender: e.target.value as Gender,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                    <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    required
                    value={personalData.dateOfBirth}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        dateOfBirth: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality *
                  </label>
                  <input
                    type="text"
                    required
                    value={personalData.nationality}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        nationality: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Place of Birth *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="City, State, Country"
                    value={personalData.placeOfBirth}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        placeOfBirth: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={personalData.email}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+923001234567"
                    value={personalData.whatsappNumber}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        whatsappNumber: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passport Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={personalData.passportNumber}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        passportNumber: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passport Expiry Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={personalData.passportExpiry}
                    onChange={(e) =>
                      setPersonalData({
                        ...personalData,
                        passportExpiry: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between mt-6 sm:mt-8 space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm sm:text-base"
                >
                  {loading ? "Saving..." : "Next: Upload Documents"}
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Documents */}
          {currentStep === "documents" && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                Upload Documents
              </h2>

              <div className="space-y-4">
                {requiredDocuments.map((doc) => {
                  const existingDocs = getExistingDocumentsForType(doc.type);
                  const hasExisting = existingDocs.length > 0;
                  const hasNewFiles =
                    uploadedFiles[doc.type] &&
                    uploadedFiles[doc.type]!.length > 0;
                  const hasError = documentErrors[doc.type];

                  return (
                    <div
                      key={doc.type}
                      className={`border rounded-lg p-4 ${hasError
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200"
                        }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {doc.description}
                            {doc.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Accepted: PDF, JPEG, PNG • Max 10MB per file
                          </p>
                        </div>
                      </div>

                      {/* Document-specific error */}
                      {hasError && (
                        <div className="mb-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                          <div className="flex items-start">
                            <svg
                              className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-red-800 mb-1">
                                Upload Error for {doc.description}
                              </p>
                              <p className="text-sm text-red-700">{hasError}</p>
                            </div>
                            <button
                              onClick={() => {
                                setDocumentErrors((prev) => {
                                  const updated = { ...prev };
                                  delete updated[doc.type];
                                  return updated;
                                });
                              }}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Show existing uploaded documents */}
                      {hasExisting && (
                        <div className="mb-3 space-y-2">
                          <p className="text-sm font-medium text-gray-700">
                            Uploaded Documents:
                          </p>
                          {existingDocs.map((existingDoc) => (
                            <div
                              key={existingDoc.id}
                              className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <span className="text-green-600">✓</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {existingDoc.originalName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {(existingDoc.size / 1024).toFixed(2)} KB •
                                    Uploaded{" "}
                                    {new Date(
                                      existingDoc.uploadedAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {existingDoc.url && (
                                  <a
                                    href={existingDoc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                                  >
                                    View
                                  </a>
                                )}
                                <button
                                  onClick={() =>
                                    handleDeleteExistingDocument(existingDoc.id)
                                  }
                                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                                  disabled={loading}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* File upload input */}
                      <div>
                        <label className="text-sm text-gray-700 font-medium mb-2 block">
                          {hasExisting
                            ? "Upload New/Replacement Document:"
                            : "Upload Document:"}
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple={doc.type !== "PASSPORT_PHOTO"}
                          onChange={(e) =>
                            handleFileChange(doc.type, e.target.files)
                          }
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                        />
                      </div>

                      {/* Show newly selected files */}
                      {hasNewFiles && (
                        <p className="text-sm text-blue-600 mt-2">
                          📎 {uploadedFiles[doc.type]!.length} new file(s)
                          selected (will be uploaded when you click Next)
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> You can view and delete existing
                  documents, then upload new ones. Changes will be saved when
                  you click "Next" or "Save & Continue".
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between mt-6 sm:mt-8 space-y-3 sm:space-y-0">
                <button
                  onClick={() => transitionToStep("personal")}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm sm:text-base"
                >
                  Back
                </button>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={handleSaveDraft}
                    className="w-full sm:w-auto px-6 py-2 border border-cyan-600 text-cyan-600 rounded-lg hover:bg-cyan-50 text-sm sm:text-base"
                  >
                    Save Draft
                  </button>
                  <button
                    onClick={handleDocumentUpload}
                    disabled={loading}
                    className="w-full sm:w-auto px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm sm:text-base"
                  >
                    {loading ? "Saving..." : "Save & Continue"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Application Content */}
          {currentStep === "content" && (
            <form onSubmit={handleContentSubmit}>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                Application Content
              </h2>

              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Statement (Max 5000 characters) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={6}
                    required
                    maxLength={5000}
                    value={contentData.personalStatement}
                    onChange={(e) =>
                      setContentData({
                        ...contentData,
                        personalStatement: e.target.value,
                      })
                    }
                    placeholder="Tell us about yourself, your goals, and why you want to study this program..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {contentData.personalStatement?.length || 0} / 5000
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Plan (Max 3000 characters) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    maxLength={3000}
                    value={contentData.academicHistory}
                    onChange={(e) =>
                      setContentData({
                        ...contentData,
                        academicHistory: e.target.value,
                      })
                    }
                    placeholder="Describe your study plan and academic goals..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {contentData.academicHistory?.length || 0} / 3000
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Experience (Max 3000 characters)
                  </label>
                  <textarea
                    rows={4}
                    maxLength={3000}
                    value={contentData.workExperience}
                    onChange={(e) =>
                      setContentData({
                        ...contentData,
                        workExperience: e.target.value,
                      })
                    }
                    placeholder="Describe your work experience if any..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {contentData.workExperience?.length || 0} / 3000
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    References (Max 2000 characters)
                  </label>
                  <textarea
                    rows={4}
                    maxLength={2000}
                    value={contentData.references}
                    onChange={(e) =>
                      setContentData({
                        ...contentData,
                        references: e.target.value,
                      })
                    }
                    placeholder="Provide references if any..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {contentData.references?.length || 0} / 2000
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between mt-6 sm:mt-8 space-y-3 sm:space-y-0">
                <button
                  type="button"
                  onClick={handleBackToDocuments}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm sm:text-base"
                >
                  Back
                </button>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="w-full sm:w-auto px-6 py-2 border border-cyan-600 text-cyan-600 rounded-lg hover:bg-cyan-50 text-sm sm:text-base"
                  >
                    Save Draft
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm sm:text-base"
                  >
                    {loading ? "Saving..." : "Next: Review"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Step 4: Review */}
          {currentStep === "review" && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                Review Your Application
              </h2>

              <div className="space-y-4 sm:space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 text-gray-900">
                        {personalData.givenNames} {personalData.familyName}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 text-gray-900 break-all">
                        {personalData.email}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Nationality:</span>
                      <span className="ml-2 text-gray-900">
                        {personalData.nationality}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Passport:</span>
                      <span className="ml-2 text-gray-900">
                        {personalData.passportNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Please review all information
                    carefully. Once you submit, your application will be sent for
                    review by our team.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between mt-6 sm:mt-8 space-y-3 sm:space-y-0">
                <button
                  onClick={() => transitionToStep("content")}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm sm:text-base"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmitForReview}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm sm:text-base"
                >
                  {loading ? "Submitting..." : "Submit for Review"}
                </button>
              </div>
            </div>
          )}

          {/* Submission Success */}
          {submissionSuccess && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Application Submitted Successfully
              </h2>
              <p className="text-cyan-600 font-medium mb-6">Under Review by Raah  Global Pak</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8 text-left max-w-lg mx-auto space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm mt-0.5">1</div>
                  <div>
                    <p className="font-semibold text-gray-800">Application Under Review</p>
                    <p className="text-sm text-gray-600 mt-0.5">The Raah  Global Pak team will carefully evaluate your submitted personal details and uploaded documents.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm mt-0.5">2</div>
                  <div>
                    <p className="font-semibold text-gray-800">Service Fee Payment</p>
                    <p className="text-sm text-gray-600 mt-0.5">Upon acceptance, you will receive an email with instructions to pay the Raah  Global Pak service fee.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm mt-0.5">3</div>
                  <div>
                    <p className="font-semibold text-gray-800">Final Submission</p>
                    <p className="text-sm text-gray-600 mt-0.5">Once the service fee is confirmed, your application will be officially finalised and forwarded to the university.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => navigate("/my-applications")}
                  className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
                >
                  View My Applications
                </button>
                <button
                  onClick={() => navigate("/courses")}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Browse More Courses
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmModal
        isOpen={showDraftModal}
        title="Continue Draft Application?"
        message="You have an existing draft application for this course. Would you like to continue with it?"
        confirmLabel="Continue Draft"
        cancelLabel="Start Fresh"
        onConfirm={handleConfirmDraft}
        onCancel={() => { setShowDraftModal(false); setPendingDraftId(null); }}
        variant="info"
      />
      <ConfirmModal
        isOpen={showDeleteDocModal}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteDocument}
        onCancel={() => { setShowDeleteDocModal(false); setPendingDeleteDocId(null); }}
        variant="danger"
      />
    </div>
  );
};

export default ApplicationForm;
